import type { SidebarGroup } from "@plerity/dashboard-shell";
import { createShell, PORTAL_URLS, type SettingsTab } from "@plerity/dashboard-shell";
import { ConfirmProvider } from "@plerity/ui-core";
import { BarChart3, FileText, Send, Settings, ShieldCheck, TrendingUp, Users, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { OrgSwitcher } from "./components/OrgSwitcher";
import { useAdminStore } from "./lib/admin";
import { useTenantStore } from "./lib/tenant";
import { SmsSettingsTabContent } from "./routes/settings/SettingsTabContent";

// Single title-less ("standalone") section — SidebarNav renders a section with
// an empty title as flat top-level nav links. Hidden detail/edit/import routes
// stay registered (still routable) but live next to their logical parent.
const sidebarGroups: SidebarGroup[] = [
  {
    title: "",
    routes: [
      // ── Dashboard ──────────────────────────────────────────────────────
      {
        path: "sms/overview",
        label: "Overview",
        icon: BarChart3,
        loader: () => import("./routes/sms/SmsOverview"),
      },
      {
        path: "sms/analytics",
        label: "Analytics",
        icon: TrendingUp,
        loader: () => import("./routes/sms/SmsAnalytics"),
      },
      // ── Contacts ───────────────────────────────────────────────────────
      {
        path: "sms/contacts",
        label: "Contacts",
        icon: Users,
        loader: () => import("./routes/sms/Contacts"),
      },
      {
        path: "sms/contacts/import",
        label: "Import Contacts",
        icon: Users,
        loader: () => import("./routes/sms/ContactImport"),
        hidden: true,
      },
      {
        path: "sms/contacts/:contactId",
        label: "Contact Detail",
        icon: Users,
        loader: () => import("./routes/sms/ContactDetail"),
        hidden: true,
      },
      // ── Campaigns ──────────────────────────────────────────────────────
      {
        path: "sms/campaigns",
        label: "Campaigns",
        icon: Send,
        loader: () => import("./routes/sms/Campaigns"),
      },
      {
        path: "sms/campaigns/new",
        label: "New Campaign",
        icon: Send,
        loader: () => import("./routes/sms/CampaignBuilder"),
        hidden: true,
      },
      {
        path: "sms/campaigns/:campaignId/edit",
        label: "Edit Campaign",
        icon: Send,
        loader: () => import("./routes/sms/CampaignBuilder"),
        hidden: true,
      },
      {
        path: "sms/campaigns/:campaignId/report",
        label: "Campaign Report",
        icon: Send,
        loader: () => import("./routes/sms/CampaignReport"),
        hidden: true,
      },
      // ── Templates ──────────────────────────────────────────────────────
      {
        path: "sms/templates",
        label: "Templates",
        icon: FileText,
        loader: () => import("./routes/sms/Templates"),
      },
      {
        path: "sms/templates/new",
        label: "New Template",
        icon: FileText,
        loader: () => import("./routes/sms/TemplateEditor"),
        hidden: true,
      },
      {
        path: "sms/templates/:templateId/edit",
        label: "Edit Template",
        icon: FileText,
        loader: () => import("./routes/sms/TemplateEditor"),
        hidden: true,
      },
      // ── Automations ────────────────────────────────────────────────────
      {
        path: "sms/automations",
        label: "Automations",
        icon: Zap,
        loader: () => import("./routes/sms/Automations"),
      },
      {
        path: "sms/automations/new",
        label: "New Automation",
        icon: Zap,
        loader: () => import("./routes/sms/AutomationBuilder"),
        hidden: true,
      },
      {
        path: "sms/automations/:automationId/edit",
        label: "Edit Automation",
        icon: Zap,
        loader: () => import("./routes/sms/AutomationBuilder"),
        hidden: true,
      },
      {
        path: "sms/automations/:automationId/stats",
        label: "Automation Stats",
        icon: Zap,
        loader: () => import("./routes/sms/AutomationStats"),
        hidden: true,
      },
      // ── Compliance / Deliverability ────────────────────────────────────
      {
        path: "sms/compliance",
        label: "Compliance",
        icon: ShieldCheck,
        loader: () => import("./routes/sms/Compliance"),
      },
      // Hidden legacy settings redirect → /settings (the unified settings page,
      // auto-mounted by createShell via `config.settings`).
      {
        path: "sms/settings",
        label: "Settings (redirect)",
        icon: Settings,
        loader: () => import("@plerity/dashboard-shell/SettingsRedirect"),
        hidden: true,
      },
    ],
  },
];

declare const __CF_PAGES__: boolean;
const basename = typeof __CF_PAGES__ !== "undefined" && __CF_PAGES__ ? "/" : "/apps/sms";

/**
 * Header action buttons rendered in the shell's top-right area.
 * Only the SMS-specific super-admin org switcher — never ThemeToggle or
 * MobileMenuButton (the shell auto-injects those).
 */
function HeaderActions() {
  const isSuperAdmin = useAdminStore((s) => s.isSuperAdmin);
  return isSuperAdmin ? <OrgSwitcher /> : null;
}

/**
 * TenantContextLoader — Loads tenant context and admin status on mount.
 *
 * Unified auth (via createShell({ auth })) handles authentication. This
 * component only loads the tenant-specific context (org switcher, super-admin
 * detection) that the SMS operator app needs.
 */
function TenantContextLoader({ children }: { children: React.ReactNode }) {
  const fetchTenantContext = useTenantStore((s) => s.fetchTenantContext);
  const fetchOrganizations = useAdminStore((s) => s.fetchOrganizations);
  const setIsSuperAdmin = useAdminStore((s) => s.setIsSuperAdmin);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchTenantContext()
      .then(() =>
        fetchOrganizations()
          .then(() => setIsSuperAdmin(true))
          .catch((err: unknown) => {
            // 403 = not a super-admin (hide the org switcher).
            // 401 / network = preserve previous state (next API call redirects).
            // createFetcher rejects with a FLAT normalized error (.status), not
            // the raw axios `.response.status` shape — read the flat status.
            const status = err && typeof err === "object" ? (err as { status?: number }).status : undefined;
            if (status === 403) {
              setIsSuperAdmin(false);
            }
          })
      )
      .finally(() => setLoaded(true));
  }, [fetchTenantContext, fetchOrganizations, setIsSuperAdmin]);

  if (!loaded) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  return <>{children}</>;
}

const smsSettingsTabs: SettingsTab[] = [
  {
    id: "sms-settings",
    label: "SMS Settings",
    icon: Settings,
    content: <SmsSettingsTabContent />,
  },
];

const Shell = createShell({
  name: "sms",
  displayName: "SMS Marketing",
  shortTitle: "SMS",
  sidebarGroups,
  defaultRoute: "/sms/overview",
  currentApp: "sms",
  basename,
  headerActions: <HeaderActions />,
  // @allow-hardcoded — Hub SSO security settings URL; cannot be env-var in CF Pages static SPA build
  securityRoute: PORTAL_URLS.SECURITY,
  settingsRoute: "/settings",
  auth: {
    required: true,
    entitlement: "sms",
  },
  chat: {
    enabled: true,
    features: ["campaigns", "contacts", "templates", "automations", "analytics", "compliance"],
  },
  testerNotes: {
    enabled: true,
    contextId: "web-sms",
  },
  settings: {
    additionalTabs: smsSettingsTabs,
    assistantName: "SMS Assistant",
  },
});

/**
 * App root. Auth is handled entirely by createShell({ auth }). The chat
 * assistant renders via the shell's floating-widget slot.
 */
function App() {
  return (
    <ConfirmProvider>
      <TenantContextLoader>
        <Shell />
      </TenantContextLoader>
    </ConfirmProvider>
  );
}

export default App;
